import { useSyncExternalStore } from 'react';
import type { HelpSurfaceId } from '../model/surfaces';
import { helpAnchorSelector } from '../model/anchors';

/**
 * Estado da ajuda contextual, fora do React para que serviços sem
 * componente (ex.: toast de erro) possam abrir uma orientação.
 * Módulo leve: não importa o catálogo de conteúdo.
 */

export type HelpErrorHint = {
  pieceId: string;
  message: string;
  /** Escopo em que o erro aconteceu; outro escopo nunca vê o aviso. */
  scope: string;
};

type HelpState = {
  drawerOpen: boolean;
  /** Peça aberta na gaveta; vazio mostra a ajuda da tela. */
  pieceId?: string;
  tourId?: string;
  /** Subfluxos ativos (modal/aba), do mais antigo ao mais recente. */
  surfaceStack: ReadonlyArray<{ token: number; surface: HelpSurfaceId }>;
  lastError?: HelpErrorHint;
  scope: string;
  contextVersion: number;
};

type Listener = () => void;

let state: HelpState = { drawerOpen: false, surfaceStack: [], scope: '', contextVersion: 0 };
const listeners = new Set<Listener>();
let nextToken = 0;
let returnFocus: HTMLElement | undefined;

function rememberFocus(): void {
  if (state.drawerOpen || state.tourId) return;
  const active = document.activeElement;
  returnFocus = active instanceof HTMLElement && active !== document.body
    ? active
    : document.querySelector<HTMLElement>(helpAnchorSelector('shell.ajuda')) ?? undefined;
}

function setState(patch: Partial<HelpState>): void {
  state = { ...state, ...patch };
  listeners.forEach((listener) => {
    listener();
  });
}

export const helpStore = {
  getState: (): HelpState => state,

  subscribe(listener: Listener): () => void {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },

  openContextual(): void {
    rememberFocus();
    setState({ drawerOpen: true, pieceId: undefined, tourId: undefined });
  },

  openPiece(pieceId: string): void {
    rememberFocus();
    setState({ drawerOpen: true, pieceId, tourId: undefined });
  },

  closeDrawer(): void {
    setState({ drawerOpen: false });
  },

  /** Tour e gaveta não convivem: dois focos concorrentes confundem teclado e leitor de tela. */
  startTour(tourId: string): void {
    rememberFocus();
    setState({ tourId, drawerOpen: false });
  },

  endTour(): void {
    setState({ tourId: undefined });
  },

  restoreFocus(): void {
    if (!returnFocus) return;
    const target = returnFocus.isConnected ? returnFocus : document.querySelector<HTMLElement>(helpAnchorSelector('shell.ajuda'));
    returnFocus = undefined;
    target?.focus();
  },

  pushSurface(surface: HelpSurfaceId): number {
    const token = ++nextToken;
    setState({ surfaceStack: [...state.surfaceStack, { token, surface }] });
    return token;
  },

  popSurface(token: number): void {
    setState({ surfaceStack: state.surfaceStack.filter((entry) => entry.token !== token) });
  },

  recordError(hint: Omit<HelpErrorHint, 'scope'>): void {
    setState({ lastError: { ...hint, scope: state.scope } });
  },

  /** Troca de usuário/fazenda/rota: descarta contexto e invalida ações de toasts antigos. */
  setScope(scope: string): void {
    if (scope === state.scope) return;
    returnFocus = undefined;
    setState({ scope, contextVersion: state.contextVersion + 1, lastError: undefined, tourId: undefined, drawerOpen: false, pieceId: undefined });
  },
};

export function useHelpState<T>(selector: (current: HelpState) => T): T {
  return useSyncExternalStore(helpStore.subscribe, () => selector(state), () => selector(state));
}
